import { useCallback, useRef } from 'react';

import { useMutation } from '@tanstack/react-query';

import { callPost } from '@/api/fetcher';

import { TeaserSectionForm } from './types';

/**
 * "Generate teasers for this page."
 *
 * The model runs in the orchestrator, at authoring time only, and it owns every
 * judgement about which selectors will still match after the customer's next
 * deploy.
 *
 * Results are written **straight into the form**, with no review step in
 * between. Nothing is persisted until the author presses Save, so the form is
 * itself the review: they can edit a chip, delete a section, or press Preview
 * to see the result before committing to any of it. An accept/discard pass on
 * top of an unsaved form was a second confirmation for the same decision.
 */

/** What the orchestrator proposes. `fragile` is advice, and never saved. */
type SectionProposal = {
  id: string;
  name?: string;
  match: string[];
  fragile?: boolean;
  quick_replies?: string[];
};

type GenerateResponse = { sections: SectionProposal[] };

type GenerateArgs = { url: string; guidelines?: string };

/**
 * A proposal is only useful once it is in the form's own shape, so convert at
 * the boundary rather than teaching the editor a second shape.
 *
 * `name` becomes the id when the id is missing: there is no separate display
 * name on the wire, which is why the generator proposes one from the section's
 * heading in the first place.
 */
export const toSectionForm = (proposal: SectionProposal): TeaserSectionForm => ({
  id: (proposal.id || proposal.name || '').trim(),
  match: (proposal.match?.length ? proposal.match : ['']).map((value) => ({ value })),
  // Always chips. A section is a set of quick actions and nothing else — the
  // page's own teaser is what carries a line — so a proposal that came back
  // with no replies is an empty section to fill in, not a callout.
  variant: 'question_chips',
  message: '',
  placeholder: '',
  quick_replies: (proposal.quick_replies ?? ['']).map((value) => ({ value })),
  fragile: proposal.fragile,
});

type Options = {
  /**
   * Called with the generated sections, for the caller to write into its own
   * field array.
   *
   * The caller does the writing rather than this hook, because the array is
   * owned by a `useFieldArray` — `replace()` keeps its internal keys in step,
   * where a `setValue` on the array path from outside would desync them and
   * lose focus on every row.
   */
  onGenerated: (sections: TeaserSectionForm[]) => void;
  /** No section was found: a real answer, and its own state in the UI. */
  onEmpty: () => void;
};

export const useGenerateSections = (
  deskId: string | undefined,
  integrationId: string | undefined,
  { onGenerated, onEmpty }: Options
) => {
  /**
   * Cancels a request whose page row has gone away.
   *
   * Collapsing the accordion calls `reset()`, and removing a page shifts every
   * index after it, so a response that lands late would otherwise be written
   * into a row that is no longer the one it was generated for.
   */
  const abortRef = useRef<AbortController | null>(null);

  const mutation = useMutation<GenerateResponse, Error, GenerateArgs>({
    mutationFn: ({ url, guidelines }) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      return callPost(
        `/www/webchat-preview/${encodeURIComponent(deskId ?? '')}/${encodeURIComponent(integrationId ?? '')}/generate-teasers`,
        // Omitted rather than sent empty: the orchestrator only adds a
        // guidelines paragraph to the prompt when there is one.
        { url, ...(guidelines?.trim() && { guidelines: guidelines.trim() }) },
        { signal: controller.signal }
      );
    },
    onSuccess: (data) => {
      const sections = (data.sections ?? []).map(toSectionForm).filter((section) => section.id);
      if (!sections.length) {
        // An empty result is a real answer — an SPA that rendered nothing, or a
        // page with no teasable blocks — not an error.
        onEmpty();
        return;
      }
      onGenerated(sections);
    },
  });

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    mutation.reset();
  }, [mutation]);

  return {
    generate: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
    cancel,
  };
};
